import type { HumanizerVisualization } from '@ambire-common/libs/humanizer/interfaces'

import { PSEUDO_LOCALE } from '../config/localization/constants'

// Humanizer output ("Swap", token, "for", token) is translated as one sentence so
// other languages can reorder it. Styled words (actions, warnings) become
// `<n>word</n>`, plain labels stay as text, and everything else (tokens,
// addresses, chains, deadlines, bold names, message text, links) becomes a `<n/>`
// slot rendered untouched: "<0>Swap</0> <1/> for <2/>".

export type HumanizerSegment =
  | { kind: 'text'; text: string }
  | { kind: 'action'; item: HumanizerVisualization; text: string }
  | { kind: 'item'; item: HumanizerVisualization }

export type HumanizerTemplate = {
  template: string
  slots: Record<number, HumanizerVisualization>
  styled: Set<number>
}

const isStyledText = (item: HumanizerVisualization) =>
  !!item.content &&
  (item.type === 'action' ||
    item.type === 'danger' ||
    (item.type === 'label' && !!item.warning && !item.isBold))

const isPlainLabel = (item: HumanizerVisualization) =>
  item.type === 'label' && !item.isBold && !item.warning && !!item.content?.trim()

// Contents that would be read as markup by the parser can't be part of a key.
const MARKUP = /<\/?\d+\s*\/?>/

export const buildHumanizerTemplate = (
  items: (HumanizerVisualization | null | undefined)[] | undefined
): HumanizerTemplate | null => {
  if (!items?.length) return null
  const parts: string[] = []
  const slots: Record<number, HumanizerVisualization> = {}
  const styled = new Set<number>()
  let n = 0
  let hasText = false

  for (const item of items) {
    if (!item) continue
    if (isPlainLabel(item)) {
      const text = item.content!.trim()
      if (MARKUP.test(text)) return null
      parts.push(text)
      hasText = true
    } else if (isStyledText(item)) {
      const text = item.content!.trim()
      if (MARKUP.test(text)) return null
      parts.push(`<${n}>${text}</${n}>`)
      slots[n] = item
      styled.add(n)
      n += 1
      hasText = true
    } else {
      parts.push(`<${n}/>`)
      slots[n] = item
      n += 1
    }
  }

  if (!hasText) return null
  return { template: parts.join(' '), slots, styled }
}

const TOKEN = /<(\d+)>([\s\S]*?)<\/\1>|<(\d+)\s*\/>/g

export const parseTranslatedTemplate = (
  translated: string,
  slots: Record<number, HumanizerVisualization>,
  styled: Set<number> = new Set()
): HumanizerSegment[] | null => {
  if (typeof translated !== 'string' || !translated.trim()) return null
  const segments: HumanizerSegment[] = []
  const seen = new Set<number>()

  const pushText = (raw: string): boolean => {
    if (/<\/?\d+/.test(raw)) return false
    const text = raw.trim()
    if (text) segments.push({ kind: 'text', text })
    return true
  }

  let last = 0
  TOKEN.lastIndex = 0
  let match = TOKEN.exec(translated)
  while (match) {
    if (!pushText(translated.slice(last, match.index))) return null
    const isPair = match[1] !== undefined
    const index = Number(isPair ? match[1] : match[3])
    const item = slots[index]
    if (!item || seen.has(index) || isPair !== styled.has(index)) return null
    seen.add(index)
    if (isPair) {
      const text = match[2]!.trim()
      if (!text || /<\/?\d+/.test(text)) return null
      segments.push({ kind: 'action', item, text })
    } else {
      segments.push({ kind: 'item', item })
    }
    last = match.index + match[0].length
    match = TOKEN.exec(translated)
  }
  if (!pushText(translated.slice(last))) return null
  if (seen.size !== Object.keys(slots).length) return null
  return segments
}

export type HumanizerI18n = {
  language: string
  getResource: (lng: string, ns: string, key: string) => unknown
}

const isEnglish = (language: string) =>
  language !== PSEUDO_LOCALE && (language === 'en' || language.startsWith('en-'))

/**
 * Translated segments for a humanizer visualization, or null when the English
 * items should be rendered as they are (English UI, sentence not in the
 * current locale's translations, or a translation with broken markup).
 */
export const translateHumanizerVisualization = (
  items: (HumanizerVisualization | null | undefined)[] | undefined,
  i18n: HumanizerI18n,
  namespace: string
): HumanizerSegment[] | null => {
  const language = i18n.language
  if (!language || isEnglish(language)) return null
  const built = buildHumanizerTemplate(items)
  if (!built) return null
  const resource = i18n.getResource(language, namespace, built.template)
  if (typeof resource !== 'string' || !resource) return null
  return parseTranslatedTemplate(resource, built.slots, built.styled)
}

/**
 * A row label of an ERC-7730 descriptor in the current language. Only the labels listed in
 * humanizerMessages.ts (the descriptors bundled with ZKX) are translated; any other label is
 * descriptor data and is shown as it comes.
 */
export const translateErc7730FieldLabel = (
  label: string,
  isKnownLabel: boolean,
  i18n: HumanizerI18n,
  namespace: string
): string => {
  const language = i18n.language
  if (!isKnownLabel || !language || isEnglish(language)) return label
  const resource = i18n.getResource(language, namespace, label)
  return typeof resource === 'string' && resource ? resource : label
}
